import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { claimOrg, getClaimInfo } from "../api/ProspectAPI";
import { logApiError, getUserMessage } from "../api/apiError";

/**
 * Public /claim/{token}: a business takes ownership of the org BabaHanks has
 * been managing for them. The link was emailed to their known address —
 * possession of the token is the proof of ownership. Claiming creates their
 * first user on the already-existing org; their listings don't move.
 */
const ClaimPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [info, setInfo] = useState<{ orgName: string; maskedEmail: string; claimable: boolean } | null>(null);
    const [failed, setFailed] = useState(false);
    const [userName, setUserName] = useState("");
    const [password, setPassword] = useState("");
    const [password2, setPassword2] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) { setFailed(true); return; }
        getClaimInfo(token)
            .then(setInfo)
            .catch((e) => { logApiError(e); setFailed(true); });
    }, [token]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!token) return;
        if (password !== password2) {
            setError("The passwords don't match.");
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await claimOrg(token, userName, password);
            setDone(true);
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="app-layout">
            <div className="public-topbar">
                <strong className="public-brand">BabaHanks</strong>
                <div className="public-links">
                    <Link to="/" className="btn-outline">Back to Search</Link>
                </div>
            </div>
            <div className="public-body">
                <div style={{ width: "100%", maxWidth: "440px", margin: "2rem auto", padding: "0 0.75rem" }}>
                    {failed ? (
                        <div style={{ textAlign: "center" }}>
                            <h2>This claim link isn't valid</h2>
                            <p style={{ color: "var(--text-secondary)" }}>
                                It may have been mistyped. Reply to the email you received
                                from us and we'll sort it out.
                            </p>
                        </div>
                    ) : !info ? (
                        <p style={{ textAlign: "center", color: "var(--text-secondary)" }}>Loading…</p>
                    ) : done ? (
                        <div style={{ textAlign: "center" }}>
                            <h2>{info.orgName} is yours ✓</h2>
                            <p>
                                Your account is ready — your listings, contact details, and
                                location came with it. Sign in with <strong>{info.maskedEmail}</strong>{" "}
                                and the password you just chose.
                            </p>
                            <Link to="/login" style={{
                                display: "inline-block", marginTop: "0.5rem",
                                background: "var(--primary-color, #4f46e5)", color: "#fff",
                                padding: "0.6rem 1.4rem", borderRadius: "8px",
                                fontWeight: 700, textDecoration: "none",
                            }}>
                                Sign in
                            </Link>
                        </div>
                    ) : !info.claimable ? (
                        <div style={{ textAlign: "center" }}>
                            <h2>{info.orgName} is already claimed</h2>
                            <p style={{ color: "var(--text-secondary)" }}>
                                This organization is managed by its owner. If that should be
                                you, reply to the email you received from us.
                            </p>
                            <Link to="/login">Sign in</Link>
                        </div>
                    ) : (
                        <>
                            <h2 style={{ textAlign: "center" }}>Take ownership of {info.orgName}</h2>
                            <p style={{ color: "var(--text-secondary)", textAlign: "center" }}>
                                BabaHanks set up this listing page for your business. Choose a
                                password and it's yours — listings, contact details, and
                                location included. Your account email will be{" "}
                                <strong>{info.maskedEmail}</strong> (where we sent this link).
                            </p>
                            <form onSubmit={submit}>
                                <div className="form-group">
                                    <label>Your name</label>
                                    <input value={userName} onChange={(e) => setUserName(e.target.value)}
                                           autoComplete="name" disabled={busy} required />
                                </div>
                                <div className="form-group">
                                    <label>Password (at least 8 characters)</label>
                                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                                           autoComplete="new-password" disabled={busy} minLength={8} required />
                                </div>
                                <div className="form-group">
                                    <label>Repeat password</label>
                                    <input type="password" value={password2} onChange={(e) => setPassword2(e.target.value)}
                                           autoComplete="new-password" disabled={busy} minLength={8} required />
                                </div>
                                <button type="submit" disabled={busy} style={{ width: "100%", marginTop: "0.25rem" }}>
                                    {busy ? "Claiming…" : "Claim my business"}
                                </button>
                                {error && <div className="error-message" style={{ marginTop: "0.5rem" }}>{error}</div>}
                            </form>
                            <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", marginTop: "0.75rem" }}>
                                Rather not have this page at all? Reply to our email and
                                we'll remove it.
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ClaimPage;